import { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  fork,
  put,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { STORAGE_KEYS } from '../../constants';
import api, { login, logout } from '../../services/authService/authService';
import { IUser } from '../../types';
import {
  getUserDataFailure,
  getUserDataSuccess,
  getUserProfileFailure,
  getUserProfileSuccess,
  getWorkspaceDataFailure,
  getWorkspaceDataSuccess,
  loginFailure,
  loginSuccess,
  logoutFailure,
  logoutSuccess,
  registerFailure,
  registerSuccess,
  updateUserProfileFailure,
  updateUserProfileSuccess,
} from './user.action';
import { LoginCredentials, UserActionTypes } from './user.types';

// Define action interfaces for saga parameters
interface LoginAction {
  type: UserActionTypes.LOGIN_REQUEST;
  payload: LoginCredentials;
}

interface RegisterAction {
  type: UserActionTypes.REGISTER_REQUEST;
  payload: Partial<IUser>;
}

interface GetUserProfileAction {
  type: UserActionTypes.GET_USER_PROFILE_REQUEST;
  payload: string;
}

interface UpdateUserProfileAction {
  type: UserActionTypes.UPDATE_USER_PROFILE_REQUEST;
  payload: Partial<IUser>;
}

// Saga worker functions
function* loginSaga(action: LoginAction): SagaIterator {
  try {
    const response = yield call(login, action.payload);
    yield put(loginSuccess(response.data));
  } catch (error: any) {
    yield put(loginFailure(error.message));
  }
}

function* logoutSaga(): SagaIterator {
  try {
    yield call(logout);
    yield put(logoutSuccess());
  } catch (error: any) {
    yield put(logoutFailure(error.message));
  }
}

function* getUserDataSaga(): SagaIterator {
  try {
    const response = yield call(api.get, 'auth/me');

    // Handle different response structures
    let userData: IUser;
    if (response.data.user) {
      userData = response.data.user;
    } else if (response.data.data) {
      userData = response.data.data;
    } else {
      userData = response.data;
    }

    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    yield put(getUserDataSuccess(userData));
  } catch (error: any) {
    console.error('Error fetching user data:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch user data';
    yield put(getUserDataFailure(errorMessage));
  }
}

function* getWorkspaceDataSaga(): SagaIterator {
  try {
    const {
      data: { data },
    } = yield call(api.get, 'dashboard/workspace');

    localStorage.setItem(STORAGE_KEYS.WORKSPACE, JSON.stringify(data));
    yield put(getWorkspaceDataSuccess(data));
  } catch (error: any) {
    console.error('Error fetching workspace data:', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch workspace data';
    yield put(getWorkspaceDataFailure(errorMessage));
  }
}

// Keep existing register and profile sagas for backward compatibility
function* registerSaga(action: RegisterAction): SagaIterator {
  try {
    // This would need to be implemented in authService if needed
    const response = yield call(fetch, 'auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(action.payload),
    });

    if (!response.ok) {
      throw new Error('Registration failed');
    }

    const data = yield response.json();

    // Store token in localStorage if provided
    if (data.token) {
      localStorage.setItem('authToken', data.token);
    }

    yield put(registerSuccess(data.user));
  } catch (error: any) {
    yield put(registerFailure(error.message));
  }
}

function* getUserProfileSaga(action: GetUserProfileAction): SagaIterator {
  try {
    const response = yield call(fetch, `users/${action.payload}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }

    const data = yield response.json();
    yield put(getUserProfileSuccess(data.user));
  } catch (error: any) {
    yield put(getUserProfileFailure(error.message));
  }
}

function* updateUserProfileSaga(action: UpdateUserProfileAction): SagaIterator {
  try {
    const response = yield call(fetch, `users/${action.payload._id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('authToken')}`,
      },
      body: JSON.stringify(action.payload),
    });

    if (!response.ok) {
      throw new Error('Failed to update user profile');
    }

    const data = yield response.json();
    yield put(updateUserProfileSuccess(data.user));
  } catch (error: any) {
    yield put(updateUserProfileFailure(error.message));
  }
}

// Watcher sagas
function* watchLoginSaga(): SagaIterator {
  yield takeEvery(UserActionTypes.LOGIN_REQUEST, loginSaga);
}

function* watchLogoutSaga(): SagaIterator {
  yield takeEvery(UserActionTypes.LOGOUT_REQUEST, logoutSaga);
}

function* watchGetUserDataSaga(): SagaIterator {
  yield takeLatest(UserActionTypes.GET_USER_DATA_REQUEST, getUserDataSaga);
}

function* watchGetWorkspaceDataSaga(): SagaIterator {
  yield takeLatest(
    UserActionTypes.GET_WORKSPACE_DATA_REQUEST,
    getWorkspaceDataSaga
  );
}

function* watchRegisterSaga(): SagaIterator {
  yield takeEvery(UserActionTypes.REGISTER_REQUEST, registerSaga);
}

function* watchGetUserProfileSaga(): SagaIterator {
  yield takeLatest(
    UserActionTypes.GET_USER_PROFILE_REQUEST,
    getUserProfileSaga
  );
}

function* watchUpdateUserProfileSaga(): SagaIterator {
  yield takeLatest(
    UserActionTypes.UPDATE_USER_PROFILE_REQUEST,
    updateUserProfileSaga
  );
}

// Root user saga
export function* userSaga(): SagaIterator {
  yield all([
    fork(watchLoginSaga),
    fork(watchLogoutSaga),
    fork(watchGetUserDataSaga),
    fork(watchGetWorkspaceDataSaga),
    fork(watchRegisterSaga),
    fork(watchGetUserProfileSaga),
    fork(watchUpdateUserProfileSaga),
  ]);
}

export default userSaga;
